import { usePlanningStore } from '@planning/AppProvider'
import {
  CarrierVisitDirection,
  HoldReaction,
  HoldResponseDto,
  HoldRestrictionType,
  HoldStatus,
  HoldTypeResponseDto,
  HoldTypeRestrictionDto,
} from '@planning/app/api'
import { ContainerJourneyItemStore } from '@planning/pages/Issues/Stores/ContainerJourneyItemStore'
import { holdTypeService } from '@planning/services'
import _ from 'lodash'
import { observable, runInAction } from 'mobx'
import { useEffect, useState } from 'react'

export type HoldValidationContext =
  | 'GateIn'
  | 'GateOut'
  | 'TruckAppointment'
  | 'Tallyman'
  | 'InternalMove'
  | 'OfficeView'

export interface HoldEnforcementOrderInput {
  containerJourneyId?: number | null
  id?: number | null
  containerId?: number | null
  containerNumber?: string | null
}

export interface HoldEnforcementResult {
  reaction: HoldReaction | null
  holdNames: string[]
  isLoading: boolean
  isBlocking: boolean
}

let holdTypesPromise: Promise<HoldTypeResponseDto[]> | null = null
export const masterHoldTypes = observable.box<HoldTypeResponseDto[] | null>(null)

export const fetchHoldTypes = () => {
  if (!holdTypesPromise) {
    holdTypesPromise = holdTypeService.get(true)
    holdTypesPromise.then(types => runInAction(() => masterHoldTypes.set(types)))
  }
  return holdTypesPromise
}

export const invalidateHoldTypesCache = () => {
  holdTypesPromise = null
  fetchHoldTypes()
}

/**
 * Maps context to applicable restriction types.
 */
const getContextRestrictions = (ctx: HoldValidationContext, dir?: CarrierVisitDirection | null) => {
  if (ctx === 'GateOut') return [HoldRestrictionType.Exit]
  if (ctx === 'GateIn' || ctx === 'OfficeView' || ctx === 'TruckAppointment')
    return dir === CarrierVisitDirection.Outbound
      ? [HoldRestrictionType.Exit]
      : [HoldRestrictionType.Entry]
  if (ctx === 'Tallyman')
    return dir === CarrierVisitDirection.Inbound
      ? [HoldRestrictionType.Entry]
      : [HoldRestrictionType.Exit]
  if (ctx === 'InternalMove') return [HoldRestrictionType.Yard]
  return []
}

/**
 * Resolves the reaction for a specific hold against the given restriction targets.
 * Returns null if the hold type has no restriction for any of the targets (hold doesn't apply to this context).
 * Returns Warning as a safe default only when restriction data is entirely unavailable.
 */
const getHoldReaction = (
  hold: HoldResponseDto,
  targets: HoldRestrictionType[],
  master: HoldTypeResponseDto[] | null,
): HoldReaction | null => {
  if (!hold.holdType) return HoldReaction.Warning

  let restrictions: HoldTypeRestrictionDto[] | { [key: string]: HoldReaction } | null | undefined =
    hold.holdType.restrictions

  // Always prefer master list — it is the authoritative source for restrictions
  if (master) {
    const full = master.find(t => t.id === hold.holdType?.id || t.name === hold.holdType?.name)
    if (full) restrictions = full.restrictions
  }

  // No restriction data at all — unknown hold type, warn as safe default
  if (!restrictions) return HoldReaction.Warning

  // Normalize restrictions to a flat map regardless of API shape:
  // - Array shape: HoldTypeRestrictionDto[] (from master list)
  // - Object shape: Record<restrictionType, reaction> (from embedded hold data)
  const restrictionMap: Record<string, HoldReaction> = Array.isArray(restrictions)
    ? Object.fromEntries(
        restrictions
          .filter(r => r.restrictionType && r.reaction)
          .map(r => [r.restrictionType!.toLowerCase(), r.reaction]),
      )
    : Object.fromEntries(
        Object.entries(restrictions as Record<string, HoldReaction>).map(([k, v]) => [
          k.toLowerCase(),
          v,
        ]),
      )

  const matchedReactions = targets
    .map(rt => restrictionMap[rt.toLowerCase()])
    .filter((r): r is HoldReaction => r != null)

  // No matching restriction for this context — hold doesn't apply here
  if (matchedReactions.length === 0) return null

  return matchedReactions.some(r => r === HoldReaction.Block)
    ? HoldReaction.Block
    : HoldReaction.Warning
}

export const getHoldEnforcementResult = (
  holds: HoldResponseDto[],
  context: HoldValidationContext,
  direction?: CarrierVisitDirection | null,
  masterTypes: HoldTypeResponseDto[] | null = null,
): Omit<HoldEnforcementResult, 'isLoading'> => {
  const active = holds.filter(h => h.status === HoldStatus.Active)
  if (active.length === 0) return { reaction: null, holdNames: [], isBlocking: false }

  const targets = getContextRestrictions(context, direction)
  if (targets.length === 0) return { reaction: null, holdNames: [], isBlocking: false }

  // Map each active hold to its reaction for this context (null = not applicable)
  const holdReactions = active.map(h => ({
    hold: h,
    reaction: getHoldReaction(h, targets, masterTypes),
  }))

  // Only holds with a non-null reaction apply to this context
  const applicable = holdReactions.filter(r => r.reaction !== null)
  if (applicable.length === 0) return { reaction: null, holdNames: [], isBlocking: false }

  // Downgrade Block → Warning for holds that have a pending override covering all blocking targets
  const effectiveReactions = applicable.map(r => {
    if (r.reaction !== HoldReaction.Block) return r
    const pendingOverrides = r.hold.pendingOverrides ?? []
    const allBlockingTargetsCovered = targets.every(t => pendingOverrides.includes(t))
    return allBlockingTargetsCovered ? { ...r, reaction: HoldReaction.Warning as HoldReaction } : r
  })

  // Contexts where a Block reaction is downgraded to Warning — action is never fully blocked
  const warnOnlyContexts: HoldValidationContext[] = ['TruckAppointment']
  const isWarnOnly = warnOnlyContexts.includes(context)
  const hasBlock = effectiveReactions.some(r => r.reaction === HoldReaction.Block)

  return {
    reaction: hasBlock && !isWarnOnly ? HoldReaction.Block : HoldReaction.Warning,
    holdNames: _.compact(effectiveReactions.map(r => r.hold.holdType?.name)),
    isBlocking: hasBlock && !isWarnOnly,
  }
}

const resolveJourneyId = (
  item: HoldEnforcementOrderInput | undefined,
  s: ContainerJourneyItemStore,
): number | undefined => {
  if (!item) return undefined
  if (item.containerJourneyId) return item.containerJourneyId

  const fromOrderId = item.id
    ? s.journeysByInboundOrderId[item.id]?.id || s.journeysByOutboundOrderId[item.id]?.id
    : undefined
  if (fromOrderId) return fromOrderId

  const fromContainerId = item.containerId
    ? s.journeysByContainerId[item.containerId]?.id
    : undefined
  if (fromContainerId) return fromContainerId

  if (item.containerNumber) {
    const fromNumber = _(s.elements)
      .values()
      .find(j => j.container?.data.number === item.containerNumber)?.id
    if (fromNumber) return fromNumber
  }

  return undefined
}

export const useHoldEnforcement = (
  item: HoldEnforcementOrderInput | undefined,
  context: HoldValidationContext,
  direction?: CarrierVisitDirection | null,
): HoldEnforcementResult => {
  const { holdItemStore, containerJourneyItemStore } = usePlanningStore()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchHoldTypes()
  }, [])

  // Read directly from the MobX observable — reactive, no useState lag
  const masterTypes = masterHoldTypes.get()

  // Resolve the container journey ID from whatever identifier was provided
  const jId = resolveJourneyId(item, containerJourneyItemStore)

  useEffect(() => {
    if (jId || !item) return
    if (item.id) containerJourneyItemStore.fetchByOrderIds([item.id])
    else if (item.containerId) containerJourneyItemStore.fetch([item.containerId])
    else if (item.containerNumber)
      containerJourneyItemStore.fetchByContainerNumbers([item.containerNumber])
  }, [jId, item, containerJourneyItemStore])

  useEffect(() => {
    if (jId && !holdItemStore.holdsByContainerJourneyId[jId]) {
      setLoading(true)
      holdItemStore.fetchHoldsByContainerJourneyIds([jId]).finally(() => setLoading(false))
    }
  }, [jId, holdItemStore])

  const holds = jId ? (holdItemStore.holdsByContainerJourneyId[jId] ?? []) : []
  const result = getHoldEnforcementResult(holds, context, direction, masterTypes)

  return { ...result, isLoading: loading || !masterTypes || (!!item && !jId) }
}
